import { useState, useEffect, useCallback, useRef } from 'react';
import { usePusherContext } from '../lib/context/PusherContext';
import { vendorReviewService } from '../lib/services/vendorReviewService';
import type {
  VendorReviewDecision,
  VendorReviewWithMatches,
} from '../types/vendor';

export type { VendorReviewWithMatches } from '../types/vendor';

export const useVendorReview = (batchId: string, clientId: string) => {
  const [pendingReviews, setPendingReviews] = useState<
    VendorReviewWithMatches[]
  >([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { pusher, connect, disconnect } = usePusherContext();
  const isFetchingRef = useRef(false);

  const currentRequest = pendingReviews[currentIndex] || null;
  const totalReviews = pendingReviews.length;
  const currentReviewNumber = currentIndex + 1;
  const hasMoreReviews = currentIndex < pendingReviews.length - 1;

  const paginationInfo = {
    currentPage: currentRequest?.currentPage ?? currentReviewNumber,
    totalItems: currentRequest?.totalItems ?? totalReviews,
    pageSize: currentRequest?.pageSize ?? 1,
  };

  const fetchPendingReviews = useCallback(async () => {
    if (!clientId || !batchId || isFetchingRef.current) return;

    isFetchingRef.current = true;
    setIsLoading(true);

    try {
      const reviews = await vendorReviewService.getRequiredReviews(
        clientId,
        batchId,
      );
      setPendingReviews(reviews);
      setCurrentIndex(0);
    } catch (error) {
      console.error('Failed to fetch pending vendor reviews:', error);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [clientId, batchId]);

  useEffect(() => {
    if (!batchId) return;
    connect();
    return () => {
      disconnect();
    };
  }, [batchId, connect, disconnect]);

  useEffect(() => {
    if (!pusher || !batchId || !clientId) return;

    const channelName = `vendor-review-required-${batchId}`;
    const channel = pusher.subscribe(channelName);

    channel.bind('vendor-review-required', () => {
      fetchPendingReviews();
    });

    channel.bind('vendor-review-updated', () => {
      fetchPendingReviews();
    });

    return () => {
      channel.unbind('vendor-review-required');
      channel.unbind('vendor-review-updated');
      pusher.unsubscribe(channelName);
    };
  }, [pusher, batchId, clientId, fetchPendingReviews]);

  const submitDecision = useCallback(
    async (decision: VendorReviewDecision): Promise<void> => {
      await vendorReviewService.submitDecision(clientId, decision);

      if (currentIndex < pendingReviews.length - 1) {
        setCurrentIndex(prev => prev + 1);
      } else {
        setPendingReviews([]);
        setCurrentIndex(0);
      }
    },
    [clientId, currentIndex, pendingReviews.length],
  );

  const closeModal = useCallback(() => {
    setPendingReviews([]);
    setCurrentIndex(0);
  }, []);

  return {
    currentRequest,
    submitDecision,
    closeModal,
    totalReviews,
    currentReviewNumber,
    hasMoreReviews,
    paginationInfo,
    isLoading,
    fetchPendingReviews,
  };
};

export default useVendorReview;
